import { Device, DeviceOperation, DeviceWeldMachine } from '../types/device';

/** 1-based machine index (supports N machines). */
export type ManualMachineSlot = number;

/** @deprecated Use getManualMachineSlots(device) for dynamic N machines. */
export const MANUAL_MACHINE_SLOTS: ManualMachineSlot[] = [1, 2];

export function isManualWeldingDevice(
  device: {
    metadata?: { weldType?: string };
    deviceData?: { weld_type?: string | null };
  }
): boolean {
  const weldType = device.metadata?.weldType || device.deviceData?.weld_type || '';
  return weldType.toLowerCase().trim() === 'manual';
}

export function getManualMachineStorageKey(
  deviceId: string,
  slot: ManualMachineSlot
): string {
  return slot === 1 ? deviceId : `${deviceId}_uid${slot}`;
}

export function getManualMachineSetTimeStorageKey(
  deviceId: string,
  slot: ManualMachineSlot
): string {
  return slot === 1 ? `${deviceId}_set` : `${deviceId}_uid${slot}_set`;
}

/**
 * Prefer API `machines` array when present; otherwise synthesize the legacy
 * two-slot shape from weld_uid / weld_uid2 so existing devices keep working.
 */
export function getManualMachines(device: Device): DeviceWeldMachine[] {
  const fromArray = device.machines ?? device.deviceData?.machines;
  if (Array.isArray(fromArray) && fromArray.length > 0) {
    return fromArray;
  }

  const snapshot = parseManualLocationSnapshot(device.location?.data);
  const uid1 = device.metadata?.weldUID || device.deviceData?.weld_uid || '';
  const uid2 = device.metadata?.weldUID2 || device.deviceData?.weld_uid2 || '';

  return [
    {
      BugType: snapshot.bugType !== '-' ? snapshot.bugType : undefined,
      Weld_UID: uid1,
      Weld_Type: 'Manual',
      NumberOfLogs: device.number_of_logs?.data,
      WeldMachineConnected: device.weldMachineConnected,
    },
    {
      BugType: snapshot.bugType2 !== '-' ? snapshot.bugType2 : undefined,
      Weld_UID: uid2,
      Weld_Type: 'Manual',
      NumberOfLogs: device.number_of_logs_2?.data,
      WeldMachineConnected: device.weldMachineConnected_2,
    },
  ];
}

export function getManualMachineSlots(device: Device): ManualMachineSlot[] {
  return getManualMachines(device).map((_, index) => index + 1);
}

export function getManualMachineAt(
  device: Device,
  slot: ManualMachineSlot
): DeviceWeldMachine | undefined {
  return getManualMachines(device)[slot - 1];
}

function readMachineWeldUid(machine?: DeviceWeldMachine | null): string {
  if (!machine) return '';
  const raw = machine as DeviceWeldMachine & { weld_uid?: string; WeldUID?: string };
  return String(raw.Weld_UID || raw.weld_uid || raw.WeldUID || '').trim();
}

function readMachineWeldType(machine?: DeviceWeldMachine | null): string {
  if (!machine) return '';
  const raw = machine as DeviceWeldMachine & { weld_type?: string };
  return String(raw.Weld_Type || raw.weld_type || '').trim();
}

export function getManualMachineWeldUID(device: Device, slot: ManualMachineSlot): string {
  const fromMachine = readMachineWeldUid(getManualMachineAt(device, slot));
  if (fromMachine) return fromMachine;

  if (slot === 2) {
    return device.metadata?.weldUID2 || device.deviceData?.weld_uid2 || '';
  }
  if (slot === 1) {
    return device.metadata?.weldUID || device.deviceData?.weld_uid || '';
  }
  return '';
}

/** Weld_Type for API commands — prefer the machine entry, then device metadata. */
export function getManualMachineWeldType(device: Device, slot: ManualMachineSlot): string {
  const fromMachine = readMachineWeldType(getManualMachineAt(device, slot));
  if (fromMachine) return fromMachine;
  return device.metadata?.weldType || device.deviceData?.weld_type || 'manual';
}

export function getManualMachineConnected(device: Device, slot: ManualMachineSlot): boolean {
  const machine = getManualMachineAt(device, slot);
  if (machine && typeof machine.WeldMachineConnected === 'boolean') {
    return machine.WeldMachineConnected;
  }
  if (slot === 2) {
    return Boolean(device.weldMachineConnected_2 ?? device.deviceData?.weldMachineConnected_2);
  }
  if (slot === 1) {
    return Boolean(device.weldMachineConnected);
  }
  return false;
}

export function getManualMachineLabel(device: Device, slot: ManualMachineSlot): string {
  const machine = getManualMachineAt(device, slot);
  if (machine?.BugType) {
    return `Machine ${slot} (${machine.BugType})`;
  }

  const snapshot = parseManualLocationSnapshot(device.location?.data);
  if (slot === 1) {
    return snapshot.bugType && snapshot.bugType !== '-'
      ? `Machine 1 (${snapshot.bugType})`
      : 'Machine 1';
  }
  if (slot === 2) {
    return snapshot.bugType2 && snapshot.bugType2 !== '-'
      ? `Machine 2 (${snapshot.bugType2})`
      : 'Machine 2';
  }
  return `Machine ${slot}`;
}

export function parseManualLocationSnapshot(locationData?: string | null): {
  station: string;
  bugType: string;
  bugType2: string;
  bugNum: string;
} {
  const empty = { station: '-', bugType: '-', bugType2: '-', bugNum: '-' };
  if (!locationData) return empty;

  try {
    const parsed = JSON.parse(locationData);
    return {
      station: parsed.StationNumber || parsed.Station || '-',
      bugType: parsed.BugType || '-',
      bugType2: parsed.BugType_2 || '-',
      bugNum: parsed.BugNum || '-',
    };
  } catch {
    return empty;
  }
}

export function getManualMachineLocationFallback(
  device: Device,
  slot: ManualMachineSlot
): { station: string; bugType: string; bugNum: string } {
  const weldUID = getManualMachineWeldUID(device, slot);
  const byUid = weldUID ? device.location?.by_weld_uid?.[weldUID] : undefined;

  if (byUid?.data) {
    try {
      const parsed = JSON.parse(byUid.data);
      return {
        station: parsed.StationNumber || parsed.Station || '-',
        bugType: parsed.BugType || getManualMachineAt(device, slot)?.BugType || '-',
        bugNum: parsed.BugNum || '-',
      };
    } catch {
      // fall through
    }
  }

  const machine = getManualMachineAt(device, slot);
  const snapshot = parseManualLocationSnapshot(device.location?.data);

  if (machine?.BugType || (Array.isArray(device.machines) && device.machines.length > 0)) {
    return {
      station: snapshot.station,
      bugType: machine?.BugType || '-',
      bugNum: snapshot.bugNum,
    };
  }

  if (slot === 1) {
    const parsed = device.location?.parsed;
    return {
      station: parsed?.Station || snapshot.station,
      bugType: parsed?.BugType || snapshot.bugType,
      bugNum: parsed?.BugNum || snapshot.bugNum,
    };
  }
  if (slot === 2) {
    return {
      station: snapshot.station,
      bugType: snapshot.bugType2,
      bugNum: snapshot.bugNum,
    };
  }
  return {
    station: snapshot.station,
    bugType: machine?.BugType || '-',
    bugNum: snapshot.bugNum,
  };
}

function getByWeldUidField(
  operation: DeviceOperation | undefined,
  weldUID: string,
  dataKey: 'data' | 'error'
): string | undefined {
  if (!operation || !weldUID) return undefined;
  const entry = operation.by_weld_uid?.[weldUID];
  if (!entry) return undefined;
  const value = entry[dataKey];
  return value === undefined || value === null ? undefined : value;
}

function getDualOperationField(
  operation: DeviceOperation | undefined,
  slot: ManualMachineSlot,
  dataKey: 'data' | 'error'
): string | undefined {
  if (!operation) return undefined;
  if (slot === 1) {
    return operation[dataKey];
  }
  if (slot === 2) {
    const dualKey = dataKey === 'data' ? 'weld_uid2_data' : 'weld_uid2_error';
    return operation[dualKey as keyof DeviceOperation] as string | undefined;
  }
  return undefined;
}

function getMachineOperationField(
  device: Device,
  operation: DeviceOperation | undefined,
  slot: ManualMachineSlot,
  dataKey: 'data' | 'error'
): string | undefined {
  const weldUID = getManualMachineWeldUID(device, slot);
  const byUid = getByWeldUidField(operation, weldUID, dataKey);
  if (byUid !== undefined) return byUid;
  return getDualOperationField(operation, slot, dataKey);
}

export function getManualMachineSnapshotTime(
  device: Device,
  slot: ManualMachineSlot
): string | undefined {
  return getMachineOperationField(device, device.get_time, slot, 'data');
}

export function getManualMachineSnapshotSetTime(
  device: Device,
  slot: ManualMachineSlot
): string | undefined {
  return getMachineOperationField(device, device.set_time, slot, 'data');
}

export function getManualMachineSnapshotTimeError(
  device: Device,
  slot: ManualMachineSlot
): string | undefined {
  return getMachineOperationField(device, device.get_time, slot, 'error');
}

export function getManualMachineSnapshotSetTimeError(
  device: Device,
  slot: ManualMachineSlot
): string | undefined {
  return getMachineOperationField(device, device.set_time, slot, 'error');
}

export function getManualMachineSnapshotResetMqttError(
  device: Device,
  slot: ManualMachineSlot
): string | undefined {
  return getMachineOperationField(device, device.reset_mqtt, slot, 'error');
}

export function getManualMachineSnapshotLogCount(device: Device, slot: ManualMachineSlot): string {
  const fromMachine = getManualMachineAt(device, slot)?.NumberOfLogs;
  if (fromMachine !== undefined && fromMachine !== null && String(fromMachine) !== '') {
    return String(fromMachine);
  }
  if (slot === 1) {
    return device.number_of_logs?.data || '-';
  }
  if (slot === 2) {
    return device.number_of_logs_2?.data || '-';
  }
  return '-';
}

export function getManualMachineSnapshotLogError(
  device: Device,
  slot: ManualMachineSlot
): string | undefined {
  const weldUID = getManualMachineWeldUID(device, slot);
  const byUid = getByWeldUidField(device.number_of_logs, weldUID, 'error');
  if (byUid !== undefined) return byUid;

  if (slot === 1) {
    return device.number_of_logs?.error;
  }
  if (slot === 2) {
    return device.number_of_logs_2?.error;
  }
  return undefined;
}

export function getManualMachineSnapshotEraseError(
  device: Device,
  slot: ManualMachineSlot
): string | undefined {
  return getMachineOperationField(device, device.erase_logs, slot, 'error');
}

export function buildManualCommandPayload(
  device: Device,
  slot: ManualMachineSlot,
  base: { deviceId: string; command: string; [key: string]: unknown }
) {
  const storageDeviceId = getManualMachineStorageKey(device.id, slot);
  const weldUID = getManualMachineWeldUID(device, slot);
  const weldType = getManualMachineWeldType(device, slot);
  return {
    ...base,
    deviceId: device.id,
    storageDeviceId,
    machineSlot: slot,
    // Sent as Weld_Type / Weld_UID on /command/* — must match the selected machine
    weldType,
    weldUID,
  };
}

/** Responsive grid classes for N machine panels. */
export function getManualMachineGridClass(machineCount: number): string {
  if (machineCount <= 1) return 'grid grid-cols-1 gap-6';
  if (machineCount === 2) return 'grid grid-cols-1 md:grid-cols-2 gap-6';
  return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6';
}
